/**
 * Ledger page — org-wide cash-book.
 *
 * Mirrors the mobile Ledger at `app/(app)/more/ledger.tsx`:
 *   Header: "Ledger" + period label + Download Report CTA
 *   Period chips: All / Today / Week / Month / Last Month / Custom
 *   Custom date inputs (visible only when Custom is active)
 *   Totals card: IN / OUT / NET (3 columns)
 *   Filter chips: type (all / in / out) + project picker
 *   Date-grouped transaction list (date header + rows)
 *   Click row → opens TransactionSlideOver
 *
 * Reads via `useOrgTransactions(orgId)` — server rules already scope
 * the result set to projects the caller can see, so admins get
 * everything and non-admins get their assigned projects.
 */
import { useEffect, useMemo, useState } from 'react';

import { canFinance } from '@shared/perms';

import { PagePlaceholder } from '@/components/PagePlaceholder';
import { TransactionSlideOver } from '@/components/TransactionSlideOver';
import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import {
  buildLedgerPeriod,
  buildLedgerReportHtml,
  computeLedgerTotals,
  periodFilter,
  type LedgerPeriodKey,
} from '@/lib/ledgerReport';
import { openHtmlForPrint } from '@/lib/projectReports';
import {
  categoryLabel,
  paymentMethodLabel,
} from '@/lib/transactions';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { useOrgTransactions } from '@/lib/useOrgTransactions';
import { useProjects } from '@/lib/useProjects';
import {
  normaliseTxType,
  txDateMs,
  type TransactionItem,
} from '@/lib/useProjectTransactions';

const PERIOD_CHIPS: { key: LedgerPeriodKey; label: string }[] = [
  { key: 'all',        label: 'All time' },
  { key: 'today',      label: 'Today' },
  { key: 'this_week',  label: 'This week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'custom',     label: 'Custom' },
];

type TypeFilter = 'all' | 'in' | 'out';

const TYPE_CHIPS: { key: TypeFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'in',  label: 'In' },
  { key: 'out', label: 'Out' },
];

function fmtInr(rs: number): string {
  return '₹' + Math.round(rs).toLocaleString('en-IN');
}

function fmtDayHeader(ms: number): string {
  return new Date(ms).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function LedgerPage() {
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const orgData = (org?.data ?? {}) as {
    name?: string;
    gstin?: string;
    address?: string;
  };
  const orgName = orgData.name ?? 'Your Studio';

  // Role gate — Ledger is a financial-report surface. Server
  // rules already scope individual transaction reads per role,
  // but a Designer / Viewer URL-hopping here would see a partial
  // org cash-book + the "Download Report" CTA. Gate to the same
  // set that gets the Finance hub (SA / Admin / Manager /
  // Accountant) via the shared helper.
  const { role: orgRole } = useCurrentOrgRole();
  const canSeeLedger = canFinance(orgRole);

  const { transactions, loading } = useOrgTransactions(orgId);
  const { projects } = useProjects();

  // Quick lookup: project id → project name.
  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, p.name);
    return m;
  }, [projects]);

  // Filter state.
  const [periodKey, setPeriodKey] = useState<LedgerPeriodKey>('this_month');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all'); // 'all' or projectId
  const [openTxnId, setOpenTxnId] = useState<string | null>(null);

  // Topbar search.
  const { query: search, setPlaceholder } = useTopbarSearch();
  useEffect(() => {
    setPlaceholder('Search transactions by party, description, ref…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  // Resolve the period to ms bounds.
  const period = useMemo(
    () =>
      buildLedgerPeriod(periodKey, {
        fromKey: customFrom,
        toKey: customTo,
      }),
    [periodKey, customFrom, customTo],
  );

  // Apply all filters in order: period → type → project → search.
  const filtered = useMemo(() => {
    let rows = periodFilter(transactions, period);
    if (typeFilter !== 'all') {
      rows = rows.filter((t) => normaliseTxType(t.type) === typeFilter);
    }
    if (projectFilter !== 'all') {
      rows = rows.filter((t) => t.projectId === projectFilter);
    }
    const q = (search ?? '').trim().toLowerCase();
    if (q) {
      rows = rows.filter((t) => {
        const hay = [
          t.description ?? '',
          t.partyName ?? '',
          t.referenceNumber ?? '',
          projectNameById.get(t.projectId) ?? '',
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions, period, typeFilter, projectFilter, search, projectNameById]);

  const totals = useMemo(() => computeLedgerTotals(filtered), [filtered]);

  // Group by day (descending — newest day first).
  const grouped = useMemo(() => {
    const map = new Map<string, { ms: number; rows: TransactionItem[] }>();
    for (const t of filtered) {
      const ms = txDateMs(t);
      const k = dayKey(ms);
      const bucket = map.get(k);
      if (bucket) {
        bucket.rows.push(t);
      } else {
        // Use the first transaction's date as the day reference.
        const d = new Date(ms);
        d.setHours(0, 0, 0, 0);
        map.set(k, { ms: d.getTime(), rows: [t] });
      }
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => b.ms - a.ms);
    return arr;
  }, [filtered]);

  function handleDownloadReport() {
    if (!orgId || filtered.length === 0) return;
    const html = buildLedgerReportHtml({
      orgName,
      orgGstin: orgData.gstin,
      orgAddress: orgData.address,
      period,
      txns: filtered,
      projectNameById,
      generatedOn: new Date().toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
    });
    openHtmlForPrint(html, `${orgName} — Ledger ${period.label}`);
  }

  // Project filter dropdown options.
  const sortedProjects = useMemo(
    () =>
      [...projects].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [projects],
  );

  // Role gate render — placed AFTER the hook calls so the
  // rules-of-hooks invariant holds. orgRole resolves on first
  // render; non-finance roles see the placeholder immediately.
  if (orgRole && !canSeeLedger) {
    return (
      <div className="page active" id="page-ledger" style={{ display: 'block' }}>
        <PagePlaceholder
          title="Ledger"
          body="Ledger is a finance surface. Ask a Super Admin, Admin, or Manager to grant you access."
        />
      </div>
    );
  }

  return (
    <div className="page active" id="page-ledger" style={{ display: 'block' }}>
      {/* Header */}
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Ledger</h1>
          <div className="projects-stats">
            {orgName} · {period.label}
            {totals.count > 0
              ? ` · ${totals.count} ${totals.count === 1 ? 'entry' : 'entries'}`
              : ''}
          </div>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={handleDownloadReport}
          disabled={!orgId || filtered.length === 0}
          title="Download a printable ledger statement for this period"
        >
          <i className="ph-bold ph-download-simple" /> Download Report
        </button>
      </div>

      {/* Period chips */}
      <div className="filter-bar" style={{ marginBottom: 12 }}>
        {PERIOD_CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`filter-chip${periodKey === c.key ? ' active' : ''}`}
            onClick={() => setPeriodKey(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Custom date inputs */}
      {periodKey === 'custom' ? (
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            marginBottom: 16,
            padding: 12,
            background: '#FAFAFA',
            border: '1px solid var(--border)',
            borderRadius: 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            Range
          </span>
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.currentTarget.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          />
          <span style={{ color: 'var(--text-tertiary)' }}>→</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.currentTarget.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          />
        </div>
      ) : null}

      {/* Totals card */}
      <div
        className="card"
        style={{
          padding: 0,
          overflow: 'hidden',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            padding: 16,
          }}
        >
          <TotalCell
            label="MONEY IN"
            value={totals.inPaise}
            tone="#0F9D58"
          />
          <Divider />
          <TotalCell
            label="MONEY OUT"
            value={totals.outPaise}
            tone="#B91C1C"
          />
          <Divider />
          <TotalCell
            label="NET BALANCE"
            value={totals.netPaise}
            tone={
              totals.netPaise > 0
                ? '#0F9D58'
                : totals.netPaise < 0
                  ? '#B91C1C'
                  : 'var(--text-primary)'
            }
            signed
          />
        </div>
      </div>

      {/* Type + project filter row */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          {TYPE_CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`filter-chip${typeFilter === c.key ? ' active' : ''}`}
              onClick={() => setTypeFilter(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <select
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.currentTarget.value)}
          style={{
            padding: '6px 12px',
            border: '1px solid var(--border)',
            borderRadius: 999,
            fontSize: 12,
            fontFamily: 'inherit',
            background: '#fff',
            color: 'var(--text-primary)',
            cursor: 'pointer',
          }}
        >
          <option value="all">All projects</option>
          {sortedProjects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {/* Transaction list */}
      {loading && transactions.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          Loading ledger…
        </div>
      ) : grouped.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>
            <i className="ph ph-receipt" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
            No transactions match these filters
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              maxWidth: 420,
              margin: '0 auto',
            }}
          >
            Try a different period or clear the type / project filters above.
            Transactions show up here as soon as the studio posts them on
            any project.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {grouped.map((g) => (
            <DayGroup
              key={g.ms}
              dayMs={g.ms}
              rows={g.rows}
              projectNameById={projectNameById}
              onOpenTxn={(id) => setOpenTxnId(id)}
            />
          ))}
        </div>
      )}

      {/* Transaction detail */}
      <TransactionSlideOver
        txnId={openTxnId}
        onClose={() => setOpenTxnId(null)}
      />
    </div>
  );
}

// ───── Day group ─────

function DayGroup({
  dayMs,
  rows,
  projectNameById,
  onOpenTxn,
}: {
  dayMs: number;
  rows: TransactionItem[];
  projectNameById: Map<string, string>;
  onOpenTxn: (id: string) => void;
}) {
  let dayIn = 0;
  let dayOut = 0;
  for (const r of rows) {
    const tp = normaliseTxType(r.type);
    if (tp === 'in') dayIn += r.amount;
    else if (tp === 'out') dayOut += r.amount;
  }
  const dayNet = dayIn - dayOut;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          background: '#F9FAFB',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          {fmtDayHeader(dayMs)}
        </div>
        <div style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#0F9D58',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          +{fmtInr(dayIn)}
        </span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#B91C1C',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          −{fmtInr(dayOut)}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color:
              dayNet > 0
                ? '#0F9D58'
                : dayNet < 0
                  ? '#B91C1C'
                  : 'var(--text-primary)',
            fontVariantNumeric: 'tabular-nums',
            paddingLeft: 8,
            borderLeft: '1px solid var(--border)',
          }}
        >
          {dayNet >= 0 ? '+' : '−'}
          {fmtInr(Math.abs(dayNet))}
        </span>
      </div>

      <div>
        {rows.map((t) => (
          <TxRow
            key={t.id}
            t={t}
            projectName={projectNameById.get(t.projectId)}
            onClick={() => onOpenTxn(t.id)}
          />
        ))}
      </div>
    </div>
  );
}

function TxRow({
  t,
  projectName,
  onClick,
}: {
  t: TransactionItem;
  projectName: string | undefined;
  onClick: () => void;
}) {
  const tp = normaliseTxType(t.type);
  const isIn = tp === 'in';
  const tone = isIn ? '#0F9D58' : tp === 'out' ? '#B91C1C' : 'var(--text-secondary)';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderTop: '1px solid var(--border)',
        cursor: 'pointer',
        background: 'transparent',
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#FAFAFA';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: isIn ? '#E3F5EB' : tp === 'out' ? '#FEE2E2' : '#F1F5F9',
          color: tone,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        <i
          className={
            isIn
              ? 'ph-fill ph-arrow-down-right'
              : tp === 'out'
                ? 'ph-fill ph-arrow-up-right'
                : 'ph ph-receipt'
          }
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 280,
            }}
          >
            {t.partyName || t.description || 'Transaction'}
          </span>
          {projectName ? (
            <span
              style={{
                fontSize: 11,
                color: 'var(--text-secondary)',
                background: '#F3F4F6',
                padding: '1px 7px',
                borderRadius: 999,
                fontWeight: 600,
              }}
            >
              {projectName}
            </span>
          ) : null}
        </div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-tertiary)',
            marginTop: 2,
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          {t.category ? (
            <span>{categoryLabel(t.category as never)}</span>
          ) : null}
          {t.paymentMethod ? (
            <>
              <span>·</span>
              <span>{paymentMethodLabel(t.paymentMethod as never)}</span>
            </>
          ) : null}
          {t.description && t.description !== (t.partyName || '') ? (
            <>
              <span>·</span>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 260,
                }}
              >
                {t.description}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: tone,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}
      >
        {isIn ? '+' : tp === 'out' ? '−' : ''}
        {fmtInr(t.amount)}
      </div>
    </div>
  );
}

// ───── Totals helpers ─────

function TotalCell({
  label,
  value,
  tone,
  signed = false,
}: {
  label: string;
  value: number;
  tone: string;
  signed?: boolean;
}) {
  const display = signed
    ? `${value > 0 ? '+' : value < 0 ? '−' : ''}${fmtInr(Math.abs(value))}`
    : fmtInr(value);
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: tone,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.5px',
        }}
      >
        {display}
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          letterSpacing: 0.6,
          marginTop: 4,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        width: 1,
        background: 'var(--border)',
        marginLeft: 8,
        marginRight: 8,
      }}
    />
  );
}
